import SVG from "react-inlinesvg";
import { Spinner } from "src/sudokit/components/Spinner";
import { Text } from "src/sudokit/components/Text";
import { cn } from "src/lib/cn";
import type { EntityImageListItem } from "./lib/types";

// Inline so no theme's Text color class can win over it; the overlay always sits on a dark scrim.
const WHITE_TEXT = { color: "#fff", WebkitTextFillColor: "#fff", opacity: 1, mixBlendMode: "normal" } as const;

const BAR_BUTTON_CLASS_NAME =
  "m-0 flex h-8 w-8 items-center justify-center rounded-full border-0 !bg-transparent p-0 text-white shadow-none outline-none transition-colors focus-visible:!outline focus-visible:!outline-2 focus-visible:!outline-offset-0 focus-visible:!outline-white aria-disabled:cursor-default aria-disabled:opacity-40 [&:not([aria-disabled=true]):hover]:!bg-white/20 [&_path]:!fill-white";

export function hasLightboxInfo(image: EntityImageListItem): boolean {
  return !!(image.stylePresetLabel || image.userDescription);
}

interface LightboxControlsProps {
  image: EntityImageListItem;
  imageNoun: string;
  canGoPrev: boolean;
  canGoNext: boolean;
  awaitingNextPage: boolean;
  infoOpen: boolean;
  onToggleInfo: () => void;
  onPrev: () => void;
  onNext: () => void;
}

export default function LightboxControls({ image, imageNoun, canGoPrev, canGoNext, awaitingNextPage, infoOpen, onToggleInfo, onPrev, onNext }: LightboxControlsProps) {
  const showInfoButton = infoOpen || hasLightboxInfo(image);
  const showNavigation = canGoPrev || canGoNext;
  if (!showInfoButton && !showNavigation) return null;

  return (
    <div className="!absolute inset-x-4 bottom-4 flex min-w-0 flex-col items-start gap-2">
      {infoOpen && hasLightboxInfo(image) && (
        <div
          role="region"
          aria-label={`${imageNoun} details`}
          className="box-border max-h-[50%] w-fit min-w-0 max-w-[80%] overflow-y-auto rounded-3xl bg-black/70 px-6 py-5 text-white shadow-lg ring-1 ring-inset ring-white/25 backdrop-blur-md"
        >
          {image.stylePresetLabel && (
            <div className="mb-4 last:mb-0">
              <Text
                as="h3"
                size="lg"
                weight="semibold"
                style={WHITE_TEXT}
              >
                Style
              </Text>
              <Text style={WHITE_TEXT}>{image.stylePresetLabel}</Text>
            </div>
          )}
          {image.userDescription && (
            <div>
              <Text
                as="h3"
                size="lg"
                weight="semibold"
                style={WHITE_TEXT}
              >
                Prompt
              </Text>
              <Text
                className="whitespace-pre-wrap"
                style={WHITE_TEXT}
              >{image.userDescription}</Text>
            </div>
          )}
        </div>
      )}
      <div className="flex items-center gap-2">
        {showInfoButton && (
          <button
            type="button"
            aria-label={infoOpen ? "Hide info" : "Show info"}
            aria-expanded={infoOpen}
            className={cn(BAR_BUTTON_CLASS_NAME, "!bg-black/70 shadow-lg ring-1 ring-inset ring-white/25 backdrop-blur-md")}
            onClick={onToggleInfo}
          >
            <SVG
              src={infoOpen ? "/assets/close.svg" : "/assets/info.svg"}
              className={infoOpen ? "h-6 w-6" : "h-5 w-5"}
              aria-hidden
            />
          </button>
        )}
        {showNavigation && (
          <div className="flex items-center rounded-full bg-black/70 p-0.5 shadow-lg ring-1 ring-inset ring-white/25 backdrop-blur-md">
            <button
              type="button"
              aria-label={`Previous ${imageNoun}`}
              className={cn(BAR_BUTTON_CLASS_NAME, "h-7 w-7")}
              onClick={canGoPrev ? onPrev : undefined}
              aria-disabled={!canGoPrev || undefined}
            >
              <SVG
                src="/assets/chevron-right.svg"
                className="h-5 w-5 rotate-180"
                aria-hidden
              />
            </button>
            {canGoNext && awaitingNextPage ? (
              <div className="flex h-7 w-7 items-center justify-center">
                <Spinner
                  size="sm"
                  label={`Loading more ${imageNoun}s`}
                />
              </div>
            ) : (
              <button
                type="button"
                aria-label={`Next ${imageNoun}`}
                className={cn(BAR_BUTTON_CLASS_NAME, "h-7 w-7")}
                onClick={canGoNext ? onNext : undefined}
                aria-disabled={!canGoNext || undefined}
              >
                <SVG
                  src="/assets/chevron-right.svg"
                  className="h-5 w-5"
                  aria-hidden
                />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
